import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, X, Check, Clock } from "lucide-react";
import MenuProductCard from "../components/MenuProductCard";
import ProductModal from "../components/ProductModal";
import CartBar from "../components/CartBar";
import CheckoutPage from "../components/CheckoutPage";
import TimingPopover from "../components/TimingPopover";
import useIdentity from "../hooks/useIdentity";
import useCart from "../hooks/useCart";
import { getStorefrontBrand, getCollections, getProducts, checkout, resolveUrl, trackPageView, BRAND_COLOR } from "../webOrderApi";

// Public web-ordering storefront — this app serves exactly ONE seller, fixed
// by its own .env (REACT_APP_SELLER_ID/REACT_APP_STOREFRONT_API_KEY — see
// webOrderApi.js), not a URL param. Two ways a customer arrives here:
//   1. From an existing WhatsApp conversation — the link carries
//      ?cp={{customer_phone}}, so the session is resolved silently in the
//      background before anything renders, exactly as before.
//   2. A direct/marketing link with no prior conversation — no phone in the
//      URL. Before falling back to fully anonymous, this now checks the
//      365-day returning-customer cookie (see resolveSessionCookie) — set on
//      a past visit here where the phone WAS known (flow 1), it lets a
//      later ad click on the same browser still be recognized. No cookie
//      (or it's expired/no longer valid) behaves exactly as before:
//      prepareAnonymousWebCheckout (server side) stashes the cart in a
//      standalone, session-less row keyed by a token, and the customer's
//      phone number is only ever learned for the first time when their
//      WhatsApp confirmation message actually arrives — that's what
//      bootstraps a real ChatSession for them (see the anonymous-token
//      matching block in chatflow-engine.service.ts's runFlowLocked).
//      Either way the chat-flow itself runs identically from that point on.
//
// Browsing UI (top bar/search/collection sidebar/product grid below) is
// what used to live on a separate /menu page — that split turned out to be
// a mistake (two different browsing designs for the same catalog), so this
// is now the only page and it carries that design directly instead of the
// earlier collapsible-list layout.

// One page-view event per browsing session (this browser tab, until closed),
// not one per raw page load — sessionStorage (unlike localStorage) clears
// itself when the tab closes, so a fresh visit later still counts, but
// hitting refresh mid-visit doesn't inflate the count.
const PAGE_VIEW_TRACKED_KEY = "web_order_page_view_tracked";

export default function WebOrderPage() {
  const navigate = useNavigate();
  const { sessionId, resolvingIdentity } = useIdentity();

  const [brand, setBrand] = useState(null);
  const [brandInvalid, setBrandInvalid] = useState(false);

  const [collections, setCollections] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [activeCollectionId, setActiveCollectionId] = useState(null);
  const pendingCollectionScrollRef = useRef(null);
  const wasProductsLoadingRef = useRef(false);

  const {
    cartItems, setCartItems, toastMessage,
    addToCart, updateQuantityByProduct, updateCartItemQty, updateGroupQuantity,
    removeCartItemsForProduct, removeInvalidCartItems,
    cartItemQuantities, groupQuantities, totalCartCount, cartItemImages,
  } = useCart(products, sessionId, resolvingIdentity, !!brand);

  const [selectedProduct, setSelectedProduct] = useState(null);
  // Set only when the product modal was opened from a Checkout line/card
  // (tapping it to change the selection), not from the product listing.
  // Two shapes, depending on the product's mode:
  //   - combination mode: { cartKey, variantId } — one specific line, lets
  //     ProductModal preselect the variant already in the cart and tells it
  //     which line to remove if the customer picks a different one.
  //   - independent mode: { isGroup: true, productId } — the WHOLE set of
  //     cart lines for that product (see CheckoutPage's grouped card), so
  //     every group's current selection is preloaded at once instead of just
  //     the one line that happened to be tapped.
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isTimingOpen, setIsTimingOpen] = useState(false);

  // Branding is account-scoped (no session/phone needed) — fetched
  // immediately either way, so both flows get a working "seller not found"
  // check independent of phone resolution.
  useEffect(() => {
    getStorefrontBrand()
      .then(setBrand)
      .catch(() => setBrandInvalid(true));
  }, []);

  // Records exactly one Page View for this visit, once branding has loaded
  // (so an invalid store link is never counted) and identity resolution
  // (URL phone OR returning-customer cookie) has settled, so sessionId
  // already holds its final value by the time this fires. sessionStorage
  // guards against a mid-visit refresh firing it again (see
  // PAGE_VIEW_TRACKED_KEY above).
  useEffect(() => {
    if (!brand || resolvingIdentity) return;
    try {
      if (sessionStorage.getItem(PAGE_VIEW_TRACKED_KEY)) return;
      sessionStorage.setItem(PAGE_VIEW_TRACKED_KEY, "1");
    } catch {
      // sessionStorage unavailable (privacy mode, etc.) — fall through and
      // track anyway rather than silently dropping the event; worst case is
      // an extra page view if this happens on every render, which it won't.
    }
    trackPageView(sessionId);
  }, [brand, resolvingIdentity, sessionId]);

  // Swap the browser tab icon to the seller's own favicon (if they've set
  // one) for the duration of this storefront page, restoring the app's
  // default icon on unmount so other routes/tabs aren't affected.
  useEffect(() => {
    if (!brand?.favicon) return;
    const link = document.querySelector("link[rel='icon']");
    if (!link) return;
    const original = link.getAttribute("href");
    link.setAttribute("href", resolveUrl(brand.favicon));
    return () => link.setAttribute("href", original);
  }, [brand?.favicon]);

  // Same idea for the page title/description + Open Graph tags, sourced
  // from the seller's Meta Title/Description (Settings → Web Order) —
  // scoped to this page only, restoring the app defaults on unmount. This
  // only affects previews generated by tools that render the page (or the
  // browser tab itself); crawlers that fetch raw HTML without executing JS
  // (e.g. WhatsApp's own link-preview fetcher) won't see it, since this app
  // has no server-side rendering for this route.
  useEffect(() => {
    if (!brand) return;
    const title = brand.metaTitle || brand.shopName;
    const description = brand.metaDescription || "";

    const originalTitle = document.title;
    document.title = title;

    const descTag = document.querySelector("meta[name='description']");
    const originalDescription = descTag?.getAttribute("content");
    if (descTag) descTag.setAttribute("content", description);

    const createdTags = [];
    const upsertOg = (property, content) => {
      let tag = document.querySelector(`meta[property='${property}']`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
        createdTags.push(tag);
      }
      tag.setAttribute("content", content);
    };
    upsertOg("og:title", title);
    upsertOg("og:description", description);

    return () => {
      document.title = originalTitle;
      if (descTag && originalDescription != null) descTag.setAttribute("content", originalDescription);
      createdTags.forEach((tag) => tag.remove());
    };
  }, [brand]);

  // Gated on `brand` only, not sessionId — browsing needs nothing beyond the
  // header-authenticated seller (see webOrderApi.js), whether or not a
  // phone/session has been resolved yet.
  useEffect(() => {
    if (!brand) return;
    getCollections().then(setCollections).catch(() => setCollections([]));
  }, [brand]);

  // Fetched once (per search change) — every collection section on the page
  // groups from this SAME list client-side via each product's own
  // `collections` field, rather than one request per section.
  useEffect(() => {
    if (!brand) return;
    setProductsLoading(true);
    getProducts({ search: searchQuery })
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, [brand, searchQuery]);

  // Prune stale cart lines on page load/refresh — a product the seller has
  // since made Inactive, or configured with day-based availability that
  // excludes today (see isProductAvailableToday, server-side), no longer
  // comes back from GET /products at all; a variant that's since gone
  // Inactive is dropped from its still-active parent product's `variants`
  // array instead. Either way that cart line is no longer purchasable, so it
  // gets silently removed — everything else in the cart is left untouched.
  //
  // Deliberately a SEPARATE, unfiltered fetch (search omitted) from the
  // `products` state above, which is scoped to whatever the customer
  // currently has typed into search — validating against a search-narrowed
  // list would wrongly drop cart items that simply don't match the current
  // search term. Runs once per page load (brand only in the dep array), not
  // on every keystroke/search change.
  useEffect(() => {
    if (!brand) return;
    getProducts({})
      .then((allProducts) => {
        const productMap = new Map(allProducts.map((p) => [p.id, p]));
        setCartItems((prev) => {
          const next = prev.filter((item) => {
            const product = productMap.get(item.productId);
            if (!product) return false;
            if (item.variantId) return (product.variants || []).some((v) => v.id === item.variantId);
            return true;
          });
          return next.length === prev.length ? prev : next;
        });
      })
      .catch(() => {
        // Best-effort — if this fails, the cart just isn't re-validated this
        // load; nothing already in it is touched.
      });
    // setCartItems (from useCart) is a useState setter — always
    // referentially stable, safe to include without re-triggering this
    // effect on every render.
  }, [brand, setCartItems]);

  // Opens the picker fresh (from the product listing) — always defaults to
  // the cheapest variant, matching the card's own price.
  const openProduct = useCallback((product) => {
    setEditingCartItem(null);
    setSelectedProduct(product);
  }, []);

  // Opens the picker from a Checkout line, pre-selecting whatever variant
  // that line is already for. Checkout is a full-screen overlay at the same
  // z-index as the product picker, rendered before it in the DOM — with
  // both open at once the picker painted BEHIND Checkout, invisible until
  // Checkout was closed. Closing Checkout here first means the picker opens
  // over the main page instead, exactly like tapping a product there.
  const openProductForEdit = useCallback(
    (item) => {
      const product = products.find((p) => p.id === item.productId);
      if (!product) return;
      setIsCartOpen(false);
      setEditingCartItem({ cartKey: item.cartKey, variantId: item.variantId, zeroPriced: item.zeroPriced });
      setSelectedProduct(product);
    },
    [products]
  );

  // Opens the picker from a Checkout GROUP card (independent-mode products —
  // see CheckoutPage) — same idea as openProductForEdit, but for the whole
  // set of cart lines this product currently has, so every group's existing
  // selection is preloaded together instead of just one.
  const openProductForGroupEdit = useCallback(
    (productId) => {
      const product = products.find((p) => p.id === productId);
      if (!product) return;
      setIsCartOpen(false);
      setEditingCartItem({ isGroup: true, productId });
      setSelectedProduct(product);
    },
    [products]
  );

  // Closing (or confirming) the picker after it was opened FROM Checkout
  // should return to Checkout — otherwise the customer lands back on the
  // main product listing and has to tap "View cart" again just to see the
  // update they came here to make in the first place.
  const closeProductModal = useCallback(() => {
    const wasEditingFromCheckout = editingCartItem !== null;
    setSelectedProduct(null);
    setEditingCartItem(null);
    if (wasEditingFromCheckout) setIsCartOpen(true);
  }, [editingCartItem]);

  // The card's "-" for a combination-mode variant product (real variants,
  // not independent mode) — several different cart lines can exist for the
  // SAME product at once (e.g. 2x Small + 1x Large), so unlike a no-variant
  // or independent-mode product there's no single line "-" can unambiguously
  // adjust. Reduces whichever variant line for this product happens to come
  // first in the cart by one; "+" never uses this at all — it always
  // reopens the picker instead (see MenuProductCard's handlePlus) so the
  // customer explicitly confirms which variant an added unit is for.
  const decrementProductVariant = useCallback(
    (productId) => {
      const line = cartItems.find((i) => i.productId === productId && i.variantId);
      if (line) updateCartItemQty(line.cartKey, line.quantity - 1);
    },
    [cartItems, updateCartItemQty]
  );

  // Deliberately does NOT clear the cart on success — the order isn't
  // actually confirmed yet at this point. The backend only stashes it as
  // pending (in the customer's session if we have one, or in a standalone
  // token-keyed row if we don't — see checkout()/webOrderApi.js); the
  // customer still has to tap Send on the pre-filled WhatsApp message
  // CheckoutPage redirects to. If they back out without sending, they land
  // back here with their cart intact rather than seeing an empty page for
  // an order that never went through.
  const handlePlaceOrder = useCallback(
    async (note) => {
      const items = cartItems.map((i) => ({
        productId: i.productId,
        ...(i.variantId ? { variantId: i.variantId } : {}),
        quantity: i.quantity,
        ...(i.zeroPriced ? { zeroPriced: true } : {}),
      }));
      return checkout(sessionId, { items, note });
    },
    [cartItems, sessionId]
  );

  // The full set of a product's current cart lines, when the modal was
  // opened from a Checkout GROUP card — feeds ProductModal both the
  // per-group preselections and the shared quantity to start from (instead
  // of always resetting to 1), joined into a stable string so ProductModal's
  // init effect only re-runs when the actual set of ids changes, not on
  // every unrelated re-render of this page.
  const editingGroupItems = editingCartItem?.isGroup ? cartItems.filter((i) => i.productId === editingCartItem.productId) : [];
  const editingGroupVariantIds = editingGroupItems.map((i) => i.variantId);
  const editingGroupQuantity = editingGroupItems[0]?.quantity ?? null;

  // Same per-collection grouping the old collapsible list used — one
  // section per collection (in the exact order the backend already returns,
  // see WebOrderService.listCollections), "All Products" included, empty
  // ones skipped. Sidebar only lists collections that actually have
  // products.
  const sections = collections
    .map((c) => ({
      id: c.id,
      name: c.name,
      logoUrl: c.logoUrl,
      products: c.id === "all" ? products : products.filter((p) => (p.collections || []).includes(c.id)),
    }))
    .filter((s) => s.products.length > 0);
  const sidebarCollections = collections.filter((c) => c.productCount > 0);

  const selectCollection = (id) => {
    setActiveCollectionId(id);
    // Jumping to a collection is a different way of browsing than search —
    // leaving a stale search term active would filter that collection's own
    // products against it too, often down to nothing, which looks broken.
    // Clearing it re-triggers the products fetch (see the effect keyed on
    // [brand, searchQuery] above), so the target section won't actually
    // exist in the DOM again until that finishes — deferring the scroll to
    // the effect below instead of doing it here avoids silently no-op'ing
    // against the still-search-filtered DOM.
    if (searchQuery) {
      setSearchQuery("");
      pendingCollectionScrollRef.current = id;
      return;
    }
    const el = document.getElementById(`menu-cat-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Fires once the products refetch triggered by clearing the search above
  // (see selectCollection) has actually settled. Checking "productsLoading
  // is currently false" isn't enough on its own — the very same render that
  // clears searchQuery also runs the fetch effect for the FIRST time, which
  // still sees the OLD (pre-clear) productsLoading value until its own
  // setProductsLoading(true) takes effect a render later; naively checking
  // "!productsLoading" right away fires this too early, against the DOM's
  // still-search-filtered sections, and burns the pending flag without ever
  // actually scrolling. Tracking a real true → false transition instead
  // guarantees this only runs after that fetch has genuinely finished and
  // the DOM reflects the un-filtered product list.
  useEffect(() => {
    const wasLoading = wasProductsLoadingRef.current;
    wasProductsLoadingRef.current = productsLoading;
    if (!pendingCollectionScrollRef.current || !(wasLoading && !productsLoading)) return;
    const id = pendingCollectionScrollRef.current;
    pendingCollectionScrollRef.current = null;
    const el = document.getElementById(`menu-cat-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [productsLoading]);

  if (brandInvalid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 shadow-lg p-6 text-center space-y-2">
          <h1 className="text-lg font-extrabold text-slate-900">Store not found</h1>
          <p className="text-sm text-slate-500">This ordering link isn't valid. Please check the link and try again.</p>
        </div>
      </div>
    );
  }

  // Blank while branding loads, and while identity resolution (URL phone or
  // the returning-customer cookie) is still settling — both flows wait here
  // now, briefly, so the very first Page View always carries the right
  // sessionId instead of firing anonymous and never being corrected.
  if (!brand || resolvingIdentity) {
    return <div className="min-h-screen bg-slate-50" />;
  }

  const storefront = brand;

  return (
    <div className="flex h-screen flex-col bg-slate-50 text-slate-900 font-sans selection:bg-amber-400">
      {toastMessage && (
        <div className="fixed bottom-24 right-6 z-50 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 animate-in slide-in-from-bottom-5 duration-200">
          <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xs">
            <Check className="w-3.5 h-3.5" />
          </div>
          <span className="text-xs font-bold">{toastMessage}</span>
        </div>
      )}

      {/* Top bar — back to the landing page, small store logo, timing popup
          trigger, static veg indicator (this storefront's whole catalog is
          veg — no per-product veg/non-veg field exists, see
          MenuProductCard.jsx's own comment on the same rule). */}
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-3 py-2.5 shrink-0">
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Back"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white shadow-sm transition-transform active:scale-95"
          style={{ backgroundColor: BRAND_COLOR }}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        {storefront.logo && (
          <img
            src={resolveUrl(storefront.logo)}
            alt={storefront.shopName}
            className="h-10 w-auto shrink-0 object-contain"
          />
        )}
        {/* relative wraps the WHOLE right-side group (clock trigger + Veg
            badge), not just the button — anchoring the popover to this
            wider box means its right edge lines up with the header's real
            right edge (matching the Veg badge), giving it the full
            available width to extend leftward instead of only the
            button's own ~32px box. On a narrow phone, anchoring to just
            the button risks the popover overflowing off the LEFT edge of
            the screen since there'd be less room to its left than its own
            width. */}
        <div className="relative ml-auto flex items-center gap-2">
          {(storefront.businessHoursSummary?.length > 0 || storefront.deliveryHoursSummary?.length > 0) && (
            <button
              type="button"
              onClick={() => setIsTimingOpen((open) => !open)}
              aria-label="Store timing"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50"
            >
              <Clock className="h-4 w-4" />
            </button>
          )}
          <div className="flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-50 px-2 py-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center rounded-[3px] border-2 border-emerald-600">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" />
            </span>
            <span className="text-[10px] font-extrabold uppercase tracking-wide text-emerald-700">Veg</span>
          </div>
          <TimingPopover
            isOpen={isTimingOpen}
            onClose={() => setIsTimingOpen(false)}
            businessHoursSummary={storefront.businessHoursSummary}
            deliveryHoursSummary={storefront.deliveryHoursSummary}
          />
        </div>
      </div>

      {/* Search — genuinely filters the grid below (re-fetches via
          getProducts({ search })). */}
      <div className="border-b border-slate-200 bg-white px-3 py-2.5 shrink-0">
        <div className="relative w-full">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-500">
            <Search className="h-4 w-4 stroke-[2.2]" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search your favorite item"
            className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-10 pr-9 text-xs font-medium text-slate-900 placeholder:text-slate-500 shadow-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 sm:text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-700"
            >
              <X className="h-4 w-4 stroke-[2.5]" />
            </button>
          )}
        </div>
      </div>

      {/* Two columns: scrollable collection sidebar (left) + product grid
          grouped by collection (right) — each scrolls independently. */}
      <div className="flex min-h-0 flex-1">
        <div className="w-20 sm:w-24 shrink-0 overflow-y-auto border-r border-slate-200 bg-white">
          {sidebarCollections.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => selectCollection(c.id)}
              className={`flex w-full flex-col items-center gap-1.5 px-1.5 py-3 text-center transition-colors ${
                activeCollectionId === c.id ? "" : "hover:bg-slate-50"
              }`}
              style={activeCollectionId === c.id ? { backgroundColor: BRAND_COLOR } : undefined}
            >
              {c.logoUrl && (
                <span className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border-2 border-transparent">
                  <img src={resolveUrl(c.logoUrl)} alt="" className="h-full w-full object-cover" />
                </span>
              )}
              <span
                className={`line-clamp-2 text-[11px] font-bold leading-tight ${
                  activeCollectionId === c.id ? "text-white" : "text-slate-700"
                }`}
              >
                {c.name}
              </span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {!productsLoading && (
            <div className="space-y-4 px-3 py-3 sm:px-4">
              {sections.length === 0 ? (
                <div className="w-full rounded-2xl border border-slate-200/90 bg-white p-8 text-center text-sm text-slate-400 shadow-sm">
                  No products found.
                </div>
              ) : (
                sections.map((section) => (
                  <div key={section.id} id={`menu-cat-${section.id}`} className="scroll-mt-3">
                    <div className="mb-2 rounded-xl bg-white px-3 py-2 shadow-sm border border-slate-100">
                      <h2 className="text-sm sm:text-base font-extrabold text-slate-900">
                        {section.name} ({section.products.length})
                      </h2>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      {section.products.map((product) => {
                        const hasVariants = (product.variants || []).length > 0;
                        // Independent-mode products never have a cart line at
                        // "{productId}-base" — every one of their lines
                        // carries a real variantId — so their quantity comes
                        // from the group-quantity map instead. A combination-
                        // mode variant product CAN have several different
                        // lines at once (e.g. 2x Small + 1x Large) — those
                        // genuinely add up, unlike independent mode's shared
                        // group quantity, so its total is a straight sum.
                        const qty = product.independentVariants
                          ? groupQuantities[product.id] || 0
                          : hasVariants
                            ? cartItems.filter((i) => i.productId === product.id).reduce((sum, i) => sum + i.quantity, 0)
                            : cartItemQuantities[`${product.id}-base`] || 0;
                        return (
                          <MenuProductCard
                            key={`${section.id}-${product.id}`}
                            product={product}
                            cartQuantity={qty}
                            onAdd={addToCart}
                            onSelectProduct={openProduct}
                            onUpdateQuantity={updateQuantityByProduct}
                            onUpdateGroupQuantity={updateGroupQuantity}
                            onDecrementVariant={decrementProductVariant}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      <ProductModal
        product={selectedProduct}
        isOpen={selectedProduct !== null}
        onClose={closeProductModal}
        onAddToCart={addToCart}
        cartItemQuantities={cartItemQuantities}
        preselectVariantId={!editingCartItem?.isGroup ? editingCartItem?.variantId : null}
        editingCartKey={!editingCartItem?.isGroup ? editingCartItem?.cartKey : null}
        preselectVariantIds={editingCartItem?.isGroup ? editingGroupVariantIds : null}
        editingGroupProductId={editingCartItem?.isGroup ? editingCartItem.productId : null}
        editingGroupQuantity={editingCartItem?.isGroup ? editingGroupQuantity : null}
        onRemoveCartItem={(cartKey) => updateCartItemQty(cartKey, 0)}
        onRemoveProductGroup={removeCartItemsForProduct}
      />

      {!isCartOpen && !selectedProduct && (
        <CartBar itemCount={totalCartCount} itemImages={cartItemImages} onOpenCart={() => setIsCartOpen(true)} />
      )}

      <CheckoutPage
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        sessionId={sessionId}
        shopName={storefront.shopName}
        logo={storefront.logo}
        cartItems={cartItems}
        products={products}
        onUpdateQuantity={updateCartItemQty}
        onUpdateGroupQuantity={updateGroupQuantity}
        onEditCartItem={openProductForEdit}
        onEditGroup={openProductForGroupEdit}
        onPlaceOrder={handlePlaceOrder}
        onRemoveInvalidItems={removeInvalidCartItems}
        waNumber={storefront.sellerWhatsApp}
        gstPercent={storefront.gstPercent}
        gstInclusive={storefront.gstInclusive}
        freeDeliveryWithinKm={storefront.freeDeliveryWithinKm}
        deliveryPaused={storefront.deliveryPaused}
        deliveryHoursClosed={!!storefront.deliveryHoursEnforced && !storefront.deliveryHoursOpen}
        deliveryHoursClosedMessage={storefront.deliveryHoursUnavailableMessage}
      />
    </div>
  );
}
